import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup';
import {Form} from 'react-bootstrap';
import {toast} from 'react-toastify';
import { Button } from '@app/components';
import moment from 'moment';
import PreviewModal from '@app/pages/ops-manual/PreviewModal';
import { confirmAlert } from 'react-confirm-alert';
import { getExtension } from '@app/services/opsManual';
import fileDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import { deleteDBSDocumentById, getDBSByUserId, getDBSDocumentById, updateUserDBSDetails, updateUserSafeguarding, updateUserPVG } from '@app/services/user';

const UpdateDBSCert = () => {

    let { id, userName } = (useLocation().state || {}) as { id: any; userName: string };
    let obj: any = {}
    const [dbsDocList, setDBSDocList] = useState([]);
    const [dbsDocError, setDBSDocError] = useState('');
    const [dbsDetails, setDBSDetails] = useState(obj);
    const [dbsDocuments, setDBSDocuments] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isSafeguardingLoading, setSafeguardingLoading] = useState(false);
    const [isPVGLoading, setPVGLoading] = useState(false);
    const [url, setUrl] = useState('');
    const [filename, setFileName] = useState('');
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [previewingDocId, setPreviewingDocId] = useState<number | null>(null);
    const [docTypeFilter, setDocTypeFilter] = useState('');
    const navigate = useNavigate();
    const canAccess = useSelector((state: any) => state.auth.permissions.users);

    // Safeguarding state
    const [safeguardingDoc, setSafeguardingDoc] = useState<FileList | null>(null);
    const [safeguardingDocError, setSafeguardingDocError] = useState('');

    // PVG state
    const [pvgDoc, setPVGDoc] = useState<FileList | null>(null);
    const [pvgDocError, setPVGDocError] = useState('');

    useEffect(() => {
        if(!canAccess['update-dbs']){
            navigate('/users');
        }
        getDBSDetailsByUserId(id)

        return () => {
            setDBSDetails(obj)
            setDBSDocuments([])
        }
    }, []);

    const updateDBSDetails = async (payload) => {
        try {
            setLoading(true);
            const resp = await updateUserDBSDetails(id, payload)
            if(resp.success === true){
                setLoading(false)
                toast.success(resp?.message)
                getDBSDetailsByUserId(id)
                setDBSDocList([])
            }else{
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
          } catch (error: any) {
            setLoading(false)
            toast.error(error?.message || 'Something went wrong')
          }
    };

    const getDBSDetailsByUserId = async (id) => {
        try {
            const resp = await getDBSByUserId(id);
            if(resp.success === true){
                setDBSDetails(resp?.collections)
                if(Object.keys(resp?.collections).length > 0){
                    setDBSDocuments(resp?.collections?.dbs_documents)
                }
            }else{
                toast.error(resp?.message);
                navigate('/users');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const { handleChange, values, handleSubmit, touched, errors } = useFormik({
        initialValues: {
            user_id: id,
            cert_date: dbsDetails?.cert_date || '',
            cert_number: dbsDetails?.cert_number || '',
            update_service: dbsDetails?.update_service || false,
            status_issues: dbsDetails?.status_issues || false,
            update_service_check_date: dbsDetails?.update_service_check_date || '',
            surname_cert: dbsDetails?.surname_cert || '',
        },
        validationSchema: Yup.object({
            cert_date: Yup.string().required('Please enter DBS cert date'),
            cert_number: Yup.string().trim().required('Please enter DBS cert number'),
            update_service_check_date: Yup.string().required('Please enter DOB for update service check'),
            surname_cert: Yup.string().trim().required('Please enter surname per DBS cert'),
        }),
        onSubmit: (values) => {
            const data = new FormData();
            if (dbsDocList.length) {
                Array.from(dbsDocList).forEach((file) => {
                    data.append("dbs_documents", file);
                });
            }
            Object.keys(values).forEach(fieldName => {
                data.append(fieldName, values[fieldName]);
            })
            updateDBSDetails(data);
        },
        enableReinitialize: true
    });

    // Safeguarding formik
    const safeguardingFormik = useFormik({
        initialValues: {
            safeguarding_cert_date: dbsDetails?.safeguarding_cert_date || '',
        },
        validationSchema: Yup.object({
            safeguarding_cert_date: Yup.string().required('Please enter safeguarding certificate date'),
        }),
        onSubmit: (values) => {
            if (!safeguardingDoc || safeguardingDoc.length === 0) {
                setSafeguardingDocError('Please upload a safeguarding document');
                return;
            }
            const data = new FormData();
            data.append('safeguarding_cert_date', values.safeguarding_cert_date);
            data.append('safeguarding_document', safeguardingDoc[0]);
            handleSafeguardingUpdate(data);
        },
        enableReinitialize: true
    });

    const handleSafeguardingUpdate = async (payload) => {
        try {
            setSafeguardingLoading(true);
            const resp = await updateUserSafeguarding(id, payload);
            if (resp.success === true) {
                setSafeguardingLoading(false);
                toast.success(resp?.message);
                setSafeguardingDoc(null);
                getDBSDetailsByUserId(id);
            } else {
                setSafeguardingLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setSafeguardingLoading(false);
            toast.error(error?.message || 'Something went wrong');
        }
    };

    // PVG formik
    const pvgFormik = useFormik({
        initialValues: {},
        onSubmit: () => {
            if (!pvgDoc || pvgDoc.length === 0) {
                setPVGDocError('Please upload a PVG document');
                return;
            }
            const data = new FormData();
            data.append('pvg_document', pvgDoc[0]);
            handlePVGUpdate(data);
        }
    });

    const handlePVGUpdate = async (payload) => {
        try {
            setPVGLoading(true);
            const resp = await updateUserPVG(id, payload);
            if (resp.success === true) {
                setPVGLoading(false);
                toast.success(resp?.message);
                setPVGDoc(null);
                getDBSDetailsByUserId(id);
            } else {
                setPVGLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setPVGLoading(false);
            toast.error(error?.message || 'Something went wrong');
        }
    };

    const handleDBSDocumentChange = (e) => {
        if(e.target.files.length > 10){
            setDBSDocError('You can only upload a maximum of 10 documents')
            setDBSDocList([])
        }else{
            setDBSDocError('')
            setDBSDocList(e.target.files);
        }
    };

    const handleSafeguardingDocChange = (e) => {
        if (e.target.files.length > 0) {
            setSafeguardingDocError('');
            setSafeguardingDoc(e.target.files);
        } else {
            setSafeguardingDoc(null);
        }
    };

    const handlePVGDocChange = (e) => {
        if (e.target.files.length > 0) {
            setPVGDocError('');
            setPVGDoc(e.target.files);
        } else {
            setPVGDoc(null);
        }
    };
    
    const previewFile = async (docId, name) => {
        try{
            setPreviewingDocId(docId);
            const resp = await getDBSDocumentById(docId);
            setUrl(URL.createObjectURL(resp))
            setFileName(name)
            handleShow();
        } catch (error) {
            console.log(error)
        } finally {
            setPreviewingDocId(null);
        }
    }

    const downloadDocument = async (docId, name) => {
        try{
            const resp = await getDBSDocumentById(docId);
            fileDownload(resp, name);
        } catch (error) {
            console.log(error)
        }
    }

    const deleteDocumentConfirm = (e, docId) => {
        confirmAlert({
            title: 'Confirm to submit',
            message: 'Are you sure you want to delete this document?',
            buttons: [
                { label: 'Yes', onClick: () => deleteDocument(docId) },
                { label: 'No', onClick: () => {} }
            ]
        });
        e.preventDefault();
    };

    const deleteDocument = async (docId) => {
        try {
            setLoading(true);
            const resp = await deleteDBSDocumentById(docId);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                getDBSDetailsByUserId(id);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
          } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Something went wrong.');
          }
    };

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-primary">
                            <div className="card-header py-2">
                                <h3 className="card-title">DBS & Compliance - {userName}</h3>
                                <div className="card-tools">
                                    <Link to={'/users'} className="btn btn-sm" style={{backgroundColor: 'white', color: '#333'}}>
                                        <i className="fas fa-arrow-left mr-1"/>Back
                                    </Link>
                                </div>
                            </div>
                            <div className="card-body py-2">

                                {/* DBS Section */}
                                <form onSubmit={handleSubmit}>
                                    <h6 style={{borderLeft: '3px solid #007bff', paddingLeft: '8px', fontWeight: 600}} className="mb-2">DBS</h6>
                                    <div className='row'>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label htmlFor='cert_date' className="mb-1 small">DBS Cert. Date <span className="required">*</span></label>
                                                <Form.Control type="date" id='cert_date' name='cert_date' size="sm"
                                                    value={values.cert_date ? moment(values.cert_date).format('YYYY-MM-DD') : ''}
                                                    onChange={handleChange}
                                                />
                                                {(touched.cert_date && errors.cert_date) && <div className="text-danger small">{errors.cert_date}</div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label htmlFor="cert_number" className="mb-1 small">DBS Cert. Number <span className="required">*</span></label>
                                                <Form.Control type="text" id="cert_number" name="cert_number" size="sm"
                                                    placeholder="Enter cert number"
                                                    onChange={handleChange} value={values.cert_number}
                                                />
                                                {(touched.cert_number && errors.cert_number) && <div className="text-danger small">{errors.cert_number}</div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label htmlFor='update_service_check_date' className="mb-1 small">DOB (Update Service Check) <span className="required">*</span></label>
                                                <Form.Control type="date" id='update_service_check_date' name='update_service_check_date' size="sm"
                                                    value={values.update_service_check_date ? moment(values.update_service_check_date).format('YYYY-MM-DD') : ''}
                                                    onChange={handleChange}
                                                />
                                                {(touched.update_service_check_date && errors.update_service_check_date) && <div className="text-danger small">{errors.update_service_check_date}</div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label htmlFor="surname_cert" className="mb-1 small">Surname Per DBS Cert <span className="required">*</span></label>
                                                <Form.Control type="text" id="surname_cert" name="surname_cert" size="sm"
                                                    placeholder="Enter surname"
                                                    onChange={handleChange} value={values.surname_cert}
                                                />
                                                {(touched.surname_cert && errors.surname_cert) && <div className="text-danger small">{errors.surname_cert}</div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label className="mb-1 small d-block">Update Service</label>
                                                <div className="custom-control custom-switch mt-1">
                                                    <input type="checkbox" name='update_service' className="custom-control-input" id="update_service"
                                                        onChange={handleChange} checked={values.update_service}
                                                    />
                                                    <label className="custom-control-label" htmlFor="update_service">Yes/No</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label className="mb-1 small d-block">DBS Cert Copy Held?</label>
                                                <div className="custom-control custom-switch mt-1">
                                                    <input type="checkbox" name='status_issues' className="custom-control-input" id="status_issues"
                                                        onChange={handleChange} checked={values.status_issues}
                                                    />
                                                    <label className="custom-control-label" htmlFor="status_issues">Yes/No</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group mb-2">
                                                <label htmlFor="dbs_documents" className="mb-1 small">Upload DBS Document(s)</label>
                                                <Form.Control type="file" id="dbs_documents" size="sm" onChange={handleDBSDocumentChange} multiple/>
                                                {dbsDocError && <div className="text-danger small">{dbsDocError}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <Button type="submit" isLoading={isLoading} className="btn btn-primary btn-sm">Update DBS</Button>
                                    </div>
                                </form>

                                <hr className="my-2"/>

                                {/* Safeguarding Section */}
                                <form onSubmit={safeguardingFormik.handleSubmit}>
                                    <h6 style={{borderLeft: '3px solid #007bff', paddingLeft: '8px', fontWeight: 600}} className="mb-2">Safeguarding</h6>
                                    <div className="row">
                                        <div className="col-sm-3">
                                            <div className="form-group mb-2">
                                                <label htmlFor="safeguarding_cert_date" className="mb-1 small">Certificate Date <span className="required">*</span></label>
                                                <Form.Control type="date" id="safeguarding_cert_date" name="safeguarding_cert_date" size="sm"
                                                    value={safeguardingFormik.values.safeguarding_cert_date ? moment(safeguardingFormik.values.safeguarding_cert_date).format('YYYY-MM-DD') : ''}
                                                    onChange={safeguardingFormik.handleChange}
                                                />
                                                {(safeguardingFormik.touched.safeguarding_cert_date && safeguardingFormik.errors.safeguarding_cert_date) && (
                                                    <div className="text-danger small">{safeguardingFormik.errors.safeguarding_cert_date}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group mb-2">
                                                <label htmlFor="safeguarding_document" className="mb-1 small">Upload Document <span className="required">*</span></label>
                                                <Form.Control type="file" id="safeguarding_document" size="sm" onChange={handleSafeguardingDocChange}/>
                                                {safeguardingDocError && <div className="text-danger small">{safeguardingDocError}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <Button type="submit" isLoading={isSafeguardingLoading} className="btn btn-primary btn-sm mr-2">Update Safeguarding</Button>
                                    </div>
                                </form>

                                <hr className="my-2"/>

                                {/* PVG Section */}
                                <form onSubmit={pvgFormik.handleSubmit}>
                                    <h6 style={{borderLeft: '3px solid #007bff', paddingLeft: '8px', fontWeight: 600}} className="mb-2">PVG</h6>
                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group mb-2">
                                                <label htmlFor="pvg_document" className="mb-1 small">Upload Document <span className="required">*</span></label>
                                                <Form.Control type="file" id="pvg_document" size="sm" onChange={handlePVGDocChange}/>
                                                {pvgDocError && <div className="text-danger small">{pvgDocError}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <Button type="submit" isLoading={isPVGLoading} className="btn btn-primary btn-sm mr-2">Update PVG</Button>
                                    </div>
                                </form>

                                <hr className="my-2"/>

                                {/* DBS Documents */}
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h6 style={{borderLeft: '3px solid #007bff', paddingLeft: '8px', fontWeight: 600}} className="mb-0">Uploaded Documents</h6>
                                    <div className="d-flex align-items-center">
                                        <label className="mb-0 mr-2 small">Document Type:</label>
                                        <Form.Control as="select" size="sm" style={{width: 'auto'}} value={docTypeFilter} onChange={e => setDocTypeFilter(e.target.value)}>
                                            <option value="">All Types</option>
                                            <option value="dbs">DBS</option>
                                            <option value="safeguarding">Safeguarding</option>
                                            <option value="pvg">PVG</option>
                                        </Form.Control>
                                    </div>
                                </div>
                                {(() => {
                                    const filtered = dbsDocuments.filter((f: any) => !docTypeFilter || f.certificate_type === docTypeFilter);
                                    if (dbsDocuments.length === 0) {
                                        return <p className='text-danger small mb-2'>No documents have been uploaded for this user.</p>;
                                    }
                                    if (filtered.length === 0) {
                                        return <p className='text-muted small mb-2'>No data available for selected document type.</p>;
                                    }
                                    return (
                                    <table className="table table-bordered table-sm mb-2">
                                        <thead>
                                            <tr>
                                                <th>Document</th>
                                                <th>Document Type</th>
                                                <th>Uploaded At</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map(file => (
                                                <tr key={file?.id}>
                                                    <td>
                                                        <a href='javascript:void(0)' onClick={() => downloadDocument(file?.id, file?.name)}>
                                                            {file?.name}
                                                        </a>
                                                    </td>
                                                    <td style={{textTransform: 'uppercase'}}>{file?.certificate_type || '-'}</td>
                                                    <td>{file?.created_at ? moment(file.created_at).format('DD/MM/YYYY hh:mm A') : '-'}</td>
                                                    <td>
                                                        <a href='javascript:void(0)' onClick={(e) => deleteDocumentConfirm(e, file?.id)} title="Delete" className="mr-2">
                                                            <i className="fas fa-trash fa-sm"/>
                                                        </a>
                                                        <a href='javascript:void(0)' onClick={() => downloadDocument(file?.id, file?.name)} title="Download" className="mr-2">
                                                            <i className="fas fa-download fa-sm"/>
                                                        </a>
                                                        {(['docx', 'doc', 'xlsx', 'xls', 'wav'].findIndex((ext) => ext == getExtension(file?.name))) < 0 &&
                                                            <a href='javascript:void(0)' onClick={() => previewFile(file?.id, file?.name)} title="Preview">
                                                                {previewingDocId === file?.id
                                                                    ? <i className="fas fa-spinner fa-spin fa-sm"/>
                                                                    : <i className="fas fa-eye fa-sm"/>
                                                                }
                                                            </a>
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    );
                                })()}


                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <PreviewModal show={show} handleClose={handleClose} url={url} filename={filename} />
    </div>
  )
}

export default UpdateDBSCert
